import React from 'react';
import { View, Text, ScrollView, StyleSheet, Platform } from 'react-native';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import type { UIComponentContract } from '../../types/componentContract';

export const uiContract: UIComponentContract = {
  id: "list.items", // Match the ID used by suggest-components
  accepts: {
    capability: "list_items",
    shape: {}, // Accepts any structure (handled in component)
    required: [],
    optional: []
  },
  renders: "list",
  priority: 8,
  surface: "options",
  fallback: true
};

interface ItemsListProps {
  data?: any[] | { data?: any[] };
}

// Helper function to extract items array (outside component to avoid hook issues)
function extractItems(data: any): any[] {
  if (!data) return [];
  
  if (Array.isArray(data)) {
    return data;
  }
  
  if (typeof data === 'object' && data !== null) {
    if (Array.isArray(data.data)) {
      return data.data;
    }
    
    // Try to find array property
    try {
      const arrayKeys = Object.keys(data).filter(key => Array.isArray(data[key]));
      if (arrayKeys.length > 0) {
        return data[arrayKeys[0]];
      }
    } catch (e) {
      // Ignore errors
    }
  }
  
  return [];
}

export const ItemsList: React.FC<ItemsListProps> = (props) => {
  const theme = themeSelector();
  const { data } = props || {};
  const items = extractItems(data);

  const cardBackground = theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(40, 40, 40, 0.95)';
  const itemCardBackground = theme ? 'rgba(243, 244, 246, 0.8)' : 'rgba(55, 65, 81, 0.8)';
  const textColor = theme ? '#1F2937' : '#F9FAFB';
  const textSecondary = theme ? '#6B7280' : '#9CA3AF';
  const borderColor = theme ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)';

  if (!items || items.length === 0) {
    return (
      <View style={[styles.container, { backgroundColor: cardBackground, borderColor }]}>
        <Text style={[styles.emptyText, { color: textSecondary }]}>No items available</Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: cardBackground, borderColor }]}>
      <View style={styles.header}>
        <Text style={[styles.title, { color: textColor }]}>Items ({items.length})</Text>
      </View>
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        <View style={styles.itemsContainer}>
          {items.map((item, index) => (
            <View
              key={item.id || item._id || index}
              style={[styles.itemCard, { backgroundColor: itemCardBackground, borderColor }]}
            >
              <View style={styles.itemContent}>
                {item.name && (
                  <Text style={[styles.itemName, { color: textColor }]}>{item.name}</Text>
                )}
                {item.title && !item.name && (
                  <Text style={[styles.itemName, { color: textColor }]}>{item.title}</Text>
                )}
                {item.surname && item.name && (
                  <Text style={[styles.itemSubtext, { color: textSecondary }]}>
                    {item.name} {item.surname}
                  </Text>
                )}
                {item.email && (
                  <Text style={[styles.itemSubtext, { color: textSecondary }]}>{item.email}</Text>
                )}
                {item.phone && (
                  <Text style={[styles.itemSubtext, { color: textSecondary }]}>{item.phone}</Text>
                )}
                {item.notes && (
                  <Text style={[styles.itemDescription, { color: textSecondary }]} numberOfLines={2}>
                    {item.notes}
                  </Text>
                )}
                {item.description && !item.notes && (
                  <Text style={[styles.itemDescription, { color: textSecondary }]} numberOfLines={2}>
                    {item.description}
                  </Text>
                )}
                <View style={styles.itemMeta}>
                  {item.language && (
                    <Text style={[styles.metaText, { color: textSecondary }]}>
                      Language: {item.language}
                    </Text>
                  )}
                  {item.stargazers_count !== undefined && (
                    <Text style={[styles.metaText, { color: textSecondary }]}>
                      ⭐ {item.stargazers_count}
                    </Text>
                  )}
                  {item.updated_at && (
                    <Text style={[styles.metaText, { color: textSecondary }]}>
                      Updated: {new Date(item.updated_at).toLocaleDateString()}
                    </Text>
                  )}
                </View>
              </View>
            </View>
          ))}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 10,
    elevation: 2,
  },
  header: {
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
  },
  scrollView: {
    maxHeight: 600,
  },
  itemsContainer: {
    gap: 12,
  },
  itemCard: {
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    marginBottom: 12,
  },
  itemContent: {
    gap: 8,
  },
  itemName: {
    fontSize: 16,
    fontWeight: '600',
  },
  itemSubtext: {
    fontSize: 14,
  },
  itemDescription: {
    fontSize: 13,
    marginTop: 4,
  },
  itemMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 8,
  },
  metaText: {
    fontSize: 12,
  },
  emptyText: {
    fontSize: 14,
    textAlign: 'center',
    padding: 20,
  },
});

// Attach contract to component
(ItemsList as any).uiContract = uiContract;

export default ItemsList;

